import { Link, useRouteError } from "react-router-dom";
import { AuthFrame } from "./auth/AuthFrame";

/**
 * The two screens nobody designs and everybody eventually reaches: an address that matches no route,
 * and a route that threw while rendering.
 *
 * Until 2026-09-08 the router had neither, so both landed on React Router's own development page. It
 * says "Unexpected Application Error!" in English, in the middle of a Swedish portal, and it offers
 * no link anywhere. A client whose bookmark went stale after a path was renamed saw that, and the
 * only way out of it was the browser's back button.
 *
 * Both stand in the frame every page outside the shell uses since the client sketch of 2026-09-24
 * (`fallbackPage` in `prototype/kund/pages-auth.js`): the wordmark and one box, a heading, one line
 * and the way on. Each line lost its second sentence to the third rule in design.md section 3.
 *
 * The way out is `/overview` rather than the browser's history, because history is where the broken
 * address is. A signed-out visitor who follows it is sent to the sign-in screen by `RequireSession`,
 * which is the correct destination for them and one they can act on.
 */

/**
 * A URL that matches nothing.
 *
 * **Outside the shell, deliberately.** The shell sits behind `RequireSession`, so a not-found page
 * rendered inside it would send a signed-out visitor with a bad bookmark to the sign-in screen
 * first, where they would sign in and be returned to the address that still does not exist. It would
 * also have to draw a navigation rail around a page that belongs to no tenant. Outside, there is no
 * session to have and no rail to draw, so the page renders for anybody and says the one true thing
 * about the address they typed.
 *
 * Nothing on it is coloured as a failure, because nothing failed. The address is simply not one of
 * ours, and an alert-flavoured screen would tell a person something is broken when what happened is
 * a typo or a link that has aged.
 */
export function NotFoundRoute() {
  return (
    <AuthFrame titleId="not-found-heading">
      <h2 id="not-found-heading" className="k-auth-title">
        Sidan finns inte
      </h2>
      <p>Adressen leder ingenstans i portalen.</p>
      <Link className="button is-primary is-block" to="/overview">
        <span>Till portalen</span>
      </Link>
    </AuthFrame>
  );
}

/**
 * Anything that threw on the way to a screen.
 *
 * **Mounted at the root of the route tree and nowhere else.** A boundary placed on each branch would
 * have to be remembered every time a route is added, and the route it was forgotten on is exactly
 * the one that would throw. One at the root cannot be forgotten. It costs the navigation rail while
 * the error is showing, which is the right trade: the state that produced the error is the state the
 * rail would be rendered from.
 *
 * The error itself is logged to the console and never shown. It is a stack trace or an exception
 * message, a customer can do nothing with either, and both can carry an identifier or a fragment of
 * a query. `app/lib/errors.ts` sets that rule for every other failure in the portal and this is the
 * same rule.
 */
export function RouteErrorRoute() {
  const error = useRouteError();

  // Not rendered. Kept where a developer with the console open can read it, which is where the
  // detail is useful and where nothing about the customer is exposed by it.
  console.error("Portal route error", error);

  return (
    <AuthFrame titleId="route-error-heading" role="alert">
      <h2 id="route-error-heading" className="k-auth-title">
        Något gick fel
      </h2>
      <p>Sidan kunde inte visas. Inget av det ni gjort har gått förlorat.</p>
      <div className="k-auth-actions">
        <button type="button" className="button is-primary is-block" onClick={() => window.location.reload()}>
          <span>Försök igen</span>
        </button>
        <Link className="button is-quiet is-block" to="/overview">
          <span>Till portalen</span>
        </Link>
      </div>
    </AuthFrame>
  );
}
